/**
 * Image Upload Handler
 * 
 * Simple utility for uploading images to the server.
 * 
 * @example
 * ```typescript
 * import { uploadImage } from '../utils/helpers';
 * 
 * const handleUpload = async (file: File) => {
 *   try {
 *     const url = await uploadImage(file, 'dish', {
 *       maxSize: 5, // 5MB max
 *       allowedTypes: ['image/jpeg', 'image/png']
 *     });
 *     console.log('Uploaded:', url);
 *   } catch (error) {
 *     console.error(error.message);
 *   }
 * };
 * ```
 */

import { uploadService } from "../services";

/**
 * Upload location types for different parts of the application
 */
export type UploadLocation = 
  | "profile" 
  | "dish" 
  | "category" 
  | "restaurant" 
  | "banner"
  | "menu"
  | "gallery";

/**
 * Options for image upload
 */
export interface ImageUploadOptions {
  maxSize?: number; // in MB, default 5MB
  allowedTypes?: string[]; // default: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
}

/**
 * Default options for image upload
 */
const defaultOptions: ImageUploadOptions = {
  maxSize: 5, // 5MB
  allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
};

/**
 * Upload a single image file
 * 
 * @param file - The file to upload
 * @param location - The upload location (profile, dish, category, etc.)
 * @param options - Optional validation options (maxSize, allowedTypes)
 * @returns Promise with the uploaded image URL
 * 
 * @example
 * ```typescript
 * const imageUrl = await uploadImage(file, 'dish');
 * // With options
 * const imageUrl = await uploadImage(file, 'dish', { 
 *   maxSize: 5, 
 *   allowedTypes: ['image/jpeg', 'image/png'] 
 * });
 * ```
 */
export const uploadImage = async (
  file: File,
  location: UploadLocation,
  options: ImageUploadOptions = {}
): Promise<string> => {
  const { maxSize, allowedTypes } = { ...defaultOptions, ...options };

  // Validate file type
  if (allowedTypes && !allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
  }

  // Validate file size
  const fileSizeInMB = file.size / (1024 * 1024);
  if (maxSize && fileSizeInMB > maxSize) {
    throw new Error(`File size exceeds ${maxSize}MB. Current size: ${fileSizeInMB.toFixed(2)}MB`);
  }

  try {
    const response = await uploadService.uploadImage(file, location);

    console.log("response", response)
    
    if (response.status && response.data?.link) {
      return response.data.link;
    } else {
      throw new Error(response.data.message || 'Upload failed');
    }
  } catch (error: any) {
    console.error('Image upload error:', error);
    throw new Error(error.message || 'Failed to upload image');
  }
};

/**
 * Upload multiple image files
 * 
 * @param files - Array of files to upload
 * @param location - The upload location
 * @param options - Optional validation options
 * @returns Promise with array of uploaded image URLs
 * 
 * @example
 * ```typescript
 * const imageUrls = await uploadMultipleImages(files, 'dish');
 * ```
 */
export const uploadMultipleImages = async (
  files: File[],
  location: UploadLocation,
  options: ImageUploadOptions = {}
): Promise<string[]> => {
  try {
    const uploadPromises = files.map((file) => uploadImage(file, location, options));
    return await Promise.all(uploadPromises);
  } catch (error: any) {
    console.error('Multiple images upload error:', error);
    throw new Error(error.message || 'Failed to upload images');
  }
};
